import { Box, Stack, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { formatCurrency } from '../../../../helpers/currency-helpers';
import { ViewBy } from '../../../../store/financials-store/financials-store.type';
import { baseWhite, inkLight } from '../../../../styles/design-tokens';
import { getViewByLabels } from './shared.type';

function CustomTooltip({
  active,
  payload,
  label,
  filterFn,
  nameFormatter,
  valueFormatter,
  getStrokeColor,
  viewBy,
  currencyCode,
}: {
  active?: boolean;
  payload?: Payload<string, string>[];
  label?: string;
  nameFormatter: (item: Payload<string, string>) => string;
  valueFormatter?: (item: Payload<string, string>) => string;
  filterFn: (item: Payload<string, string>) => boolean;
  getStrokeColor?: (item: Payload<string, string>) => string | undefined;
  viewBy?: ViewBy;
  currencyCode?: string;
}) {
  const i18n = useTranslation();
  const viewByLabels = useMemo(() => getViewByLabels(i18n.t), [i18n.t]);
  if (active && payload && payload.length) {
    const isFinancial = viewBy === ViewBy.TasksInUSD || viewBy === ViewBy.PointsInUSD;
    const viewByLabel = viewBy
      ? isFinancial
        ? `${viewByLabels[viewBy]} in ${currencyCode}`
        : viewByLabels[viewBy]
      : '';

    return (
      <Box bg={baseWhite} p={10} style={{ border: `1px solid ${inkLight}` }}>
        <Stack gap={4}>
          <Text>{label ? dayjs(label).locale('en').format('MMM, YYYY') : 'N/A'}</Text>
          {viewByLabel && (
            <Text fz="xs" c="dimmed">
              {viewByLabel}
            </Text>
          )}
          {payload
            .filter((item) => filterFn(item))
            .filter((item) => Boolean(item.name) && item.value !== undefined)
            .map((item) => {
              const color = getStrokeColor?.(item) || (item as unknown as { stroke?: string }).stroke || item.color;
              let formattedValue: string;

              if (viewBy && currencyCode) {
                formattedValue = isFinancial
                  ? formatCurrency(item.value!, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                      currency: currencyCode,
                    })
                  : new Intl.NumberFormat().format(Math.round(Number(item.value!)));
              } else if (valueFormatter) {
                formattedValue = valueFormatter(item);
              } else {
                formattedValue = String(item.value!);
              }

              return <Text key={String(item.dataKey)} c={color}>{`${nameFormatter(item)}: ${formattedValue}`}</Text>;
            })}
        </Stack>
      </Box>
    );
  }

  return null;
}

export { CustomTooltip };
